import './style.scss';
import React, {useEffect, useState} from "react";

import UserProfile from "../../components/userProfile";
import {
  clearAllUserProfileResetPasswordAlerts,
  clearSaveMyProfileErrors,
  clearSaveUserProfileAlerts,
  clearSaveUserProfileErrors,
  clearSendInviteAlerts,
  clearUpdateUserPermissionsAlerts,
  clearUserFormErrors,
  fetchMyProfile, fetchPermissionsList,
  getManagedUser,
  saveUserProfile,
  sendInviteEmail,
  sendUserProfilePasswordReset
} from "../../actions/users";
import {useDispatch, useSelector} from "react-redux";
import EditUserProfile from "./editUserProfile";
import {clearAllForgotPasswordAlerts, sendForgotPassword, sendResetPasswordLink} from "../../actions/session";
import Alert from "../../components/common/alert";
import {MESSAGES} from "../../constants";
import {buildPermissionDistricts, buildPermissionSchools, isUserASuperUser, userCanAccess} from "../../utils/users";
import UserPermissions from "../../components/userPermissions";
import EditUserPermissions from "./editUserPermissions";
import {fetchDistricts} from "../../actions/districts";
import {getLockedOrExpiredErrorMessage} from "../../utils/session";

export const route = 'user-profile';

const ManageUserProfile = () => {
  const dispatch = useDispatch();

  const managedUser = useSelector((state) => state.users.managedUser);
  const currentUser = useSelector((state) => state.session.currentUser);

  const [profileEdit, setProfileEdit] = useState(false);
  const [permissionsListLoaded, setPermissionsListLoaded] = useState(false);
  const [permissionEdit, setPermissionEdit] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const errors = useSelector((state) => state.users.userFormErrors);

  useEffect(() => {

    async function fetchUserAndPermissionsList() {
      await dispatch(fetchPermissionsList());
      await setPermissionsListLoaded(true);

      if (managedUser) {
        const user = {
          'username': managedUser,
          'apiError': 'SUCCESS'
        }

        await dispatch(fetchMyProfile(user));
        await dispatch(clearSaveUserProfileAlerts());
      } else {
        await dispatch(fetchDistricts());
        await dispatch(getManagedUser());
      }
    }

    fetchUserAndPermissionsList();
  }, [managedUser, dispatch]);

  const permissionsList = useSelector((state) => state.users.permissionsList);
  const districts = useSelector((state) => state.districts.districts);
  const userProfile = useSelector((state) => state.users.myProfile);
  const currentUserComposite = useSelector((state) => state.session.currentUserComposite);
  const saveUserProfileSuccess = useSelector((state) => state.users.saveUserProfileSuccess);
  const saveUserProfileErrors = useSelector((state) => state.users.saveUserProfileErrors);
  const sendResetPasswordSucceeded = useSelector((state) => state.users.sendUserProfileResetPasswordSuccess);
  const sendResetPasswordError = useSelector((state) => state.users.sendUserProfileResetPasswordError);
  const saveUserPermissionsErrors = useSelector((state) => state.users.saveUserPermissionsErrors);
  const updateUserPermissionsApiError = useSelector((state) => state.users.updateUserPermissionsApiFailure);
  const updateUserPermissionsApiSuccess = useSelector((state) => state.users.updateUserPermissionsApiSuccess);

  const sessionId = useSelector((state) => state.session.sessionData);

  const onSubmitAction = (profileForm, myProfile) => {
    dispatch(saveUserProfile(profileForm, userProfile));
    setFormSubmitted(true);
  }

  const onDismissAlert = () => {
    dispatch(clearSaveUserProfileErrors());
  }

  const onSubmitPermissions = async () => {
    await setPermissionEdit(false);
    await dispatch(fetchMyProfile({
      'username': managedUser,
      'apiError': 'SUCCESS'
    }));
  }

  const gotoEditAction = () => {
    setProfileEdit(true);
    dispatch(clearSaveUserProfileAlerts());
    dispatch(clearUpdateUserPermissionsAlerts());
  }

  const goToPermissionEditAction = () => {
    setPermissionEdit(true);
    dispatch(clearSaveUserProfileAlerts());
    dispatch(clearUpdateUserPermissionsAlerts());
  }

  const sendResetPasswordAction = (userToSend) => {
    dispatch(clearAllUserProfileResetPasswordAlerts());
    dispatch(sendUserProfilePasswordReset(userToSend));
  }

  if (formSubmitted && !errors) {
    setFormSubmitted(false);
    setProfileEdit(false);
  }

  let userDistricts = null;

  if (userProfile) {
    userDistricts = buildPermissionDistricts(userProfile, districts)
  }

  return (!(userProfile) ? <></> :
    <div className='main-content'>
      <div className='user-profile-header'>Manage Users / {userProfile.user.firstName} {userProfile.user.lastName}</div>
      <div className='manage-users-alerts'>
        {(saveUserProfileErrors) && <Alert messageType='error' onDismiss={() => dispatch(clearSaveUserProfileErrors())}>{MESSAGES.USERS.saveUserProfileErrors}</Alert>}
        {(saveUserProfileSuccess) && <Alert messageType='success' onDismiss={() => dispatch(clearSaveUserProfileAlerts())}>{`${MESSAGES.GENERAL.success} ${userProfile.user.firstName} ${userProfile.user.lastName}${MESSAGES.USERS.saveUserProfileSuccess}`}</Alert>}
        {(sendResetPasswordSucceeded) && <Alert messageType='success' onDismiss={() => dispatch(clearAllUserProfileResetPasswordAlerts())}>{`${MESSAGES.GENERAL.success} ${userProfile.user.firstName} ${userProfile.user.lastName} ${MESSAGES.USERS.sendResetPasswordSuccess}`}</Alert>}
        {(sendResetPasswordError) && <Alert messageType='error' onDismiss={() => dispatch(clearAllUserProfileResetPasswordAlerts())}>{getLockedOrExpiredErrorMessage(sendResetPasswordError, MESSAGES.SEND_RESET_PASSWORD)}</Alert>}
        {(updateUserPermissionsApiError) && <Alert messageType='error' onDismiss={() => dispatch(clearUpdateUserPermissionsAlerts())}>{(updateUserPermissionsApiError === 'INVALID_SCHOOL_EXISTS') ? MESSAGES.USERS.updateUserPermissionsSchoolExists : MESSAGES.USERS.saveUserPermissionsApiFailure}</Alert>}
        {(updateUserPermissionsApiSuccess) && <Alert messageType='success' onDismiss={() => dispatch(clearUpdateUserPermissionsAlerts())}>{MESSAGES.USERS.savePermissionsSuccess}</Alert>}
      </div>
      <div className='user-profile-container'>
        <div className='user-info-content'>
          {(userCanAccess(currentUserComposite, 'edit-user') && profileEdit)
            ? <EditUserProfile userProfile={userProfile} errors={errors} onSubmitAction={onSubmitAction} sendResetPasswordAction={sendResetPasswordAction} onCancelAction={() => {dispatch(clearUserFormErrors()); setProfileEdit(false)}}/>
            : <UserProfile profile={userProfile} hideEditButton={permissionEdit} gotoEditAction={gotoEditAction} sendResetPasswordAction={sendResetPasswordAction}/>
          }
        </div>
      </div>
      <div className='user-permissions-container'>
        {(!isUserASuperUser(userProfile) && userCanAccess(currentUserComposite, 'edit-user') && permissionEdit)
        ? <EditUserPermissions userProfile={userProfile} errors={saveUserPermissionsErrors} permissionsList = {permissionsList} onCancelAction={() => {dispatch(clearUserFormErrors()); setPermissionEdit(false)}} userDistricts={userDistricts} userSchools={buildPermissionSchools(userProfile, userDistricts)} districts={districts} onSubmitPermissions={onSubmitPermissions}/>
        : <UserPermissions userProfile={userProfile} hideEditButton={isUserASuperUser(userProfile) || profileEdit} goToPermissionEditAction={goToPermissionEditAction} districts={districts}/>
      }
      </div>
    </div>
  )
}

export default ManageUserProfile;